import { BoostsState, BalanceData, CardsState, DealsState, ModesState, OfferTriggersState, ShopsState, VipRecord, bossFirstClearGems, bossFirstClears, CheckIn, ChestBand, chestEssence, chestGearTargetLevel, ChestResult, DailyOrders, Fnv1a64, GearItem, ownedTowers, PlayerProfile, ProfileJson, rollChest, starChestGems, starCount, StarMask, STARS_PER_LEVEL } from "@highmarch/domain";
import { EconomyState, walletSection } from "./economyState";
import { EndgameState } from "./events";
import { AchievementsState } from "./achievements";
import { Deals } from "./deals";
import { Shops } from "./shops";
import { OfferTriggers } from "./offerTriggers";
import { modesFromSave, modesToSave } from "./modes";
import { researchFromSave, researchToSave } from "./research";
import { suppliesFromSave, suppliesToSave } from "./supplies";
import { boostsFromSave, boostsToSave } from "./boosts";
import { petsFromSave, petsToSave } from "./pets";
import { SaveRecord } from "./saves";

/** Levels per chapter: the campaign map draws ten nodes a chapter (web ui/chapters.ts splits the same way). */
export const LEVELS_PER_CHAPTER = 10;
/** Chapter star chests (ENGAGEMENT §B1): this many one-time chests per chapter, at even shares of its stars (10/20/30 of 30). */
export const STAR_CHESTS_PER_CHAPTER = 3;
/** A star chest rolls the band a first clear of the chapter's last level rolls: the best existing band, no new odds. */
const STAR_CHEST_BAND = ChestBand.FirstClear;
/** Ledger ref prefix of the one-time gem back-pay for saves from before gems (fromSave). */
const BACKPAY_REF = "backpay";
/** Seed tag, so a star chest's roll never collides with a level attempt's seed (seeds.ts hashes playerId, level, attempt). */
const STAR_CHEST_SEED_TAG = "starChest";

/** A claimed chapter star chest: the rolled chest (already banked), the gear it held and the gems it paid (D20). */
export interface StarChestClaim { readonly chest: ChestResult; readonly gear: GearItem[]; readonly gems: number }

/** Cosmetics as the Deals grants need them (web: the cosmetics feature's `Cosmetics`). */
export interface SkinHooks {
  owns(id: string): boolean;
  unlock(id: string): void;
}

/** Campaign progress: which levels exist and are unlocked, highest cleared, attempts, stars, derived tower ownership. */
export class LevelCatalog {
  readonly levelCount: number;
  /** Battle boosts running and until when (owner 2026-10-21; app boosts.ts): the `boosts` save section. */
  boostState = new BoostsState();
  private highest: number;
  private readonly attempts: number[];
  private readonly cleared: boolean[];
  /** Star conditions ever met, per level (index = level). */
  private readonly starMasks: StarMask[];
  /** Star chests claimed, per chapter (index = 0-based chapter). */
  private readonly starChestsTaken: number[];
  readonly profile: PlayerProfile;
  /** Daily Orders and the check-in calendar (meta/orders.ts, meta/checkin.ts), saved with the rest of the progress. */
  orders: DailyOrders;
  checkin: CheckIn;
  /** Gems, Marks, the Shop's and the market's day, Tribute (ADR 0013), saved in their own save sections. */
  economy: EconomyState;
  /** Event embers and claims, the week's Trial and the Heroic stars (events.ts), saved in their own save sections. */
  endgame: EndgameState;
  /** Achievements and lifetime stats (achievements.ts), saved in their own save sections. */
  achievements: AchievementsState;
  /** The Deals screen, VIP and the paid grants (deals.ts, docs/SHOP.md), saved in the `deals` and `vip` sections. */
  deals: Deals;
  /** The Mystery Merchant, the Event shop and the VIP shop (shops.ts, docs/SHOP.md §9), saved in the `shops` section. */
  shops: Shops;
  /** Iron stars and tries, the Endless bests and weekly chest (modes.ts, docs/MODES.md), saved in the `modes` section. */
  modes: ModesState;
  /** Offers suggested from the save (MP-05, offerTriggers.ts): badges only, saved in the `offerTriggers` section. */
  offerTriggers: OfferTriggers;
  private skinHooks: SkinHooks | null = null;
  /** Skins a grant paid before the cosmetics were attached: unlocked the moment they are (never lost). */
  private readonly pendingSkins: string[] = [];
  /** How a grant (Deals, the season pass's PassCosmetics) pays a skin: through the cosmetics, or held until they attach. */
  readonly skinGrants = {
    ownsSkin: (id: string): boolean => this.skinHooks?.owns(id) ?? this.pendingSkins.includes(id),
    unlockSkin: (id: string): void => { if (this.skinHooks) this.skinHooks.unlock(id); else if (!this.pendingSkins.includes(id)) this.pendingSkins.push(id); },
  };
  /**
   * Whether a change made now can be saved. The web sets it (false in a tab that lost the save to another tab):
   * Economy refuses to sell there, since what it sold would never be stored.
   */
  canPersist: () => boolean = () => true;
  get gold(): number { return this.profile.gold; }

  constructor(readonly balance: BalanceData, highestLevelCleared = 0, readonly playerId = "") {
    this.levelCount = balance.levels.length;
    this.profile = new PlayerProfile(balance);
    this.orders = new DailyOrders(balance, playerId);
    this.checkin = new CheckIn(balance, playerId);
    this.economy = EconomyState.fresh(balance);
    this.endgame = EndgameState.fresh();
    this.achievements = AchievementsState.fresh();
    this.deals = this.makeDeals(new DealsState(), new VipRecord());
    this.shops = this.makeShops(new ShopsState());
    this.modes = new ModesState();
    this.offerTriggers = new OfferTriggers(this, new OfferTriggersState());
    this.attachWallet();
    this.highest = Math.min(highestLevelCleared, this.levelCount);
    this.attempts = new Array(this.levelCount + 1).fill(0);
    this.cleared = new Array(this.levelCount + 1).fill(false);
    for (let l = 1; l <= this.highest; l++) this.cleared[l] = true;
    this.starMasks = new Array(this.levelCount + 1).fill(0);
    this.starChestsTaken = new Array(this.chapterCount).fill(0);
  }

  static fromSave(balance: BalanceData, save: SaveRecord): LevelCatalog {
    const c = new LevelCatalog(balance, save.highestLevelCleared, save.playerId);
    c.orders = DailyOrders.fromJson(balance, save.playerId, save.orders);
    c.checkin = CheckIn.fromJson(balance, save.playerId, save.checkin);
    c.economy = EconomyState.fromSave(balance, save);
    c.endgame = EndgameState.fromSave(save);
    c.achievements = AchievementsState.fromSave(save);
    const deals = Deals.stateFromSave(save);
    c.deals = c.makeDeals(deals.state, deals.vip, deals.cards);
    c.shops = c.makeShops(Shops.stateFromSave(save));
    c.modes = modesFromSave(save);
    c.offerTriggers = new OfferTriggers(c, OfferTriggers.stateFromSave(save));
    c.attachWallet();
    const loaded = PlayerProfile.fromJson(balance, save.profile as Partial<ProfileJson> | undefined);
    Object.assign(c.profile, { baseLevel: loaded.baseLevel, buildersOwned: loaded.buildersOwned, gold: loaded.gold, reductionsToday: loaded.reductionsToday, reductionDay: loaded.reductionDay });
    loaded.towerLevels.forEach((l, i) => (c.profile.towerLevels[i] = l));
    researchFromSave(save, c.profile);
    suppliesFromSave(save, c.profile);
    c.boostState = boostsFromSave(save, c);
    petsFromSave(save, c.profile, save.highestLevelCleared);
    for (const j of loaded.jobs) c.profile.jobs.push(j);
    for (const q of loaded.queue) c.profile.queue.push(q);
    for (const m of loaded.speedUps) c.profile.addSpeedUp(m);
    for (const g of loaded.gear) c.profile.gear.push(g);
    c.profile.forge.load(loaded.forge.toJson());
    (c.profile as unknown as { nextGearId: number }).nextGearId = (loaded as unknown as { nextGearId: number }).nextGearId;
    (c.profile as unknown as { nextJobId: number }).nextJobId = (loaded as unknown as { nextJobId: number }).nextJobId;
    for (const [k, v] of Object.entries(save.attempts)) {
      const level = Number(k);
      if (level >= 1 && level <= c.levelCount) c.attempts[level] = v;
    }
    for (const [k, v] of Object.entries(save.stars ?? {})) {
      const level = Number(k);
      if (level >= 1 && level <= c.levelCount) c.starMasks[level] = v;
    }
    for (const [k, v] of Object.entries(save.starChests ?? {})) {
      const chapter = Number(k) - 1;
      if (chapter >= 0 && chapter < c.chapterCount) c.starChestsTaken[chapter] = Math.min(v, STAR_CHESTS_PER_CHAPTER);
    }
    if (walletSection.read(save) === undefined) c.backPayGems(save.updatedAt);
    return c;
  }

  /**
   * A save from before gems existed has no wallet: it never got the gems its boss first clears and claimed star chests
   * pay today (D20). They are paid once, as those grants would have been, dated when the save was last written. Once:
   * the next save writes the wallet, and a save that has one is never back-paid (a page closed before that save pays
   * the same amounts again, not twice).
   */
  private backPayGems(at: number): void {
    const wallet = this.economy.wallet;
    for (const b of bossFirstClears(this.balance)) if (this.isCleared(b.level)) wallet.grantGems(b.gems, "bossFirstClear", at, `${BACKPAY_REF}.level${b.level}`);
    this.starChestsTaken.forEach((taken, chapter) => {
      for (let m = 0; m < taken; m++) wallet.grantGems(starChestGems(this.balance, m), "starChest", at, `${BACKPAY_REF}.chapter${chapter + 1}.${m + 1}`);
    });
  }

  applyTo(save: SaveRecord): void {
    save.highestLevelCleared = this.highest;
    save.profile = this.profile.toJson();
    save.orders = this.orders.toJson();
    save.checkin = this.checkin.toJson();
    this.economy.applyTo(save);
    this.endgame.applyTo(save);
    this.achievements.applyTo(save);
    this.deals.applyTo(save);
    this.shops.applyTo(save);
    modesToSave(save, this.modes);
    this.offerTriggers.applyTo(save);
    researchToSave(save, this.profile);
    suppliesToSave(save, this.profile);
    boostsToSave(save, this.boostState);
    petsToSave(save, this.profile);
    for (let l = 1; l <= this.levelCount; l++) if (this.attempts[l] > 0) save.attempts[String(l)] = this.attempts[l];
    const stars: Record<string, number> = {}, chests: Record<string, number> = {};
    for (let l = 1; l <= this.levelCount; l++) if (this.starMasks[l] > 0) stars[String(l)] = this.starMasks[l];
    this.starChestsTaken.forEach((n, ch) => { if (n > 0) chests[String(ch + 1)] = n; });
    save.stars = stars;
    save.starChests = chests;
  }

  /**
   * The weekly chest and check-in pay their gems into the wallet (D20); the VIP perks reach the rules that read them
   * (Deals.syncPerks: the profile, Tribute, orders, crates).
   */
  private attachWallet(): void {
    this.orders.wallet = this.checkin.wallet = this.economy.wallet;
    this.orders.forge = this.profile.forge;
    this.deals.syncPerks();
  }

  private makeDeals(state: DealsState, vip: VipRecord, cards?: CardsState): Deals {
    return new Deals({ catalog: this, state, vip, cards, ...this.skinGrants });
  }

  /** Whether the cosmetics hold skin `id` (false before they are attached); Deals grants and the Wyrm's reward (M40) ask. */
  ownsSkin(id: string): boolean { return this.skinGrants.ownsSkin(id); }
  /** Unlocks skin `id` as owned (and new): now, or the moment the cosmetics attach. The caller saves. */
  unlockSkin(id: string): void { this.skinGrants.unlockSkin(id); }

  /** The shops read the VIP level and its free Merchant refreshes from the `vip` section's VipRecord (docs/SHOP.md §9). */
  private makeShops(state: ShopsState): Shops {
    return new Shops({ catalog: this, state, vipLevel: () => this.deals.vipLevel(), freeRefreshes: () => this.deals.perks().freeMerchantRefreshes });
  }

  /** Attaches the cosmetics (web: the cosmetics feature at boot); skins paid before it are unlocked now. */
  attachSkins(hooks: SkinHooks): void {
    this.skinHooks = hooks;
    for (const id of this.pendingSkins.splice(0)) hooks.unlock(id);
  }

  get highestLevelCleared(): number { return this.highest; }
  get nextLevel(): number { return Math.min(this.highest + 1, this.levelCount); }
  isUnlocked(level: number): boolean { return level >= 1 && level <= this.levelCount && level <= this.highest + 1; }
  isCleared(level: number): boolean { return level >= 1 && level <= this.levelCount && this.cleared[level]; }
  attemptsOf(level: number): number { return this.attempts[level]; }
  ownedTowers(): boolean[] { return ownedTowers(this.balance, this.highest); }

  /** Records an attempt start and returns the attempt number (1-based) for seed derivation. */
  beginAttempt(level: number): number {
    if (!this.isUnlocked(level)) throw new Error(`Level ${level} is locked`);
    return ++this.attempts[level];
  }

  /**
   * OpenChest use case (§9.3): gold, one speed-up and the rolled gear land in the profile. Returns the gear.
   * `production`: the chest is a level's own battle chest (the level-end bank, web levelFlow), whose gold VIP
   * resource production raises × (1 + r) after the chest's hashed roll (docs/SHOP.md §8.3; `productionBonus` is the
   * part it adds, for the end screen's "+n VIP"). Every other chest (purchases, orders, check-in, events, shops,
   * star chests) banks as rolled. A `production` chest also pays its Forge Essence (gear v2, domain `chestEssence`).
   */
  bankChest(chest: ChestResult, production = false): GearItem[] {
    this.profile.addGold(chest.gold + (production ? this.productionBonus(chest) : 0));
    // A level's own battle chest pays Forge Essence (gear v2), outside the replay hash; bought and reward chests pay none.
    if (production) this.profile.forge.essence += chestEssence(this.balance, chest);
    this.profile.addSpeedUp(chest.speedUpMinutes);
    // Early first clears roll their gear for the seats level 5 needs, fits-now first (§9.3 early gear).
    const forLevel = chestGearTargetLevel(this.balance, chest);
    return this.profile.openChestGear(chest.level, chest.rarity, chest.gearCount, chest.seed, forLevel > 0 ? { level: forLevel, owned: this.ownedTowers() } : null);
  }

  /**
   * A rare's chest (M36, domain sim/rares.ts: `LevelSim.rareChest`, on RareKilled), banked the moment the rare dies so a
   * later loss or a closed page keeps it. Once per chest: keyed by its level and seed in the lifetime stats, so a resumed
   * battle (which replays the kill) or a checkpoint retry (which kills the same rare for the same chest) banks nothing
   * again. Banks as rolled (no VIP production, no essence: it is a reward chest). Returns the gear, or null when that
   * chest was already banked. The caller saves.
   */
  bankRareChest(chest: ChestResult): GearItem[] | null {
    if (!this.achievements.stats.noteRareChest(`${chest.level}:${chest.seed.toString(16)}`)) return null;
    return this.bankChest(chest);
  }

  /** What VIP resource production adds to a battle chest's gold when it is banked with `production` (zero at VIP 0). */
  productionBonus(chest: ChestResult): number { return this.deals.productionBonus(chest.gold); }

  /** Persistent levels per tower archetype for the sim (base included). */
  towerLevels(): number[] {
    return this.balance.towers.map((t, i) => (t.isBase ? this.profile.baseLevel : this.profile.towerLevels[i]));
  }

  /**
   * Records a level's result. A first clear of a boss's first level pays its gems (D20). With the clock: a loss starts
   * the never list's quiet window (no offers, meta/shop.ts offersAllowed) and counts towards MP-05's run of losses, and
   * Tribute starts filling once unlocked.
   */
  recordResult(level: number, won: boolean, now?: number): void {
    if (now !== undefined) { this.economy.shop.noteResult(won, now); this.offerTriggers.noteResult(level, won, now); }
    if (!won) return;
    const first = !this.isCleared(level);
    this.cleared[level] = true;
    if (level > this.highest) this.highest = level;
    if (first) this.economy.wallet.grantGems(bossFirstClearGems(this.balance, level), "bossFirstClear", now, `level${level}`);
    if (now !== undefined) this.economy.tribute.sync(now, this.highest);
  }

  // ------------------------------------------------------------------ stars (sim/stars.ts)

  /** Star conditions ever met on a level (a StarMask). */
  starsFor(level: number): StarMask { return level >= 1 && level <= this.levelCount ? this.starMasks[level] : 0; }

  /** Adds a run's stars to the level's best; returns the mask before and after (the end screen's NEW BEST). */
  recordStars(level: number, run: StarMask): { before: StarMask; after: StarMask } {
    const before = this.starsFor(level);
    if (level < 1 || level > this.levelCount) return { before, after: before };
    this.starMasks[level] = before | run;
    return { before, after: this.starMasks[level] };
  }

  get chapterCount(): number { return Math.ceil(this.levelCount / LEVELS_PER_CHAPTER); }
  chapterOfLevel(level: number): number { return Math.floor((level - 1) / LEVELS_PER_CHAPTER); }
  /** First and last level of a 0-based chapter. */
  chapterRange(chapter: number): { first: number; last: number } {
    const first = chapter * LEVELS_PER_CHAPTER + 1;
    return { first, last: Math.min(this.levelCount, first + LEVELS_PER_CHAPTER - 1) };
  }

  /** Stars earned across a chapter, and the most it holds. */
  totalStars(chapter: number): { have: number; max: number } {
    const { first, last } = this.chapterRange(chapter);
    let have = 0;
    for (let l = first; l <= last; l++) have += starCount(this.starMasks[l]);
    return { have, max: (last - first + 1) * STARS_PER_LEVEL };
  }

  /** The chapter's star chest thresholds, evenly spread over its stars (10, 20, 30 for a ten-level chapter). */
  starChestThresholds(chapter: number): number[] {
    const { max } = this.totalStars(chapter);
    return Array.from({ length: STAR_CHESTS_PER_CHAPTER }, (_, k) => Math.round((max * (k + 1)) / STAR_CHESTS_PER_CHAPTER));
  }

  starChestsClaimed(chapter: number): number { return this.starChestsTaken[chapter] ?? 0; }

  /** Whether the chapter's next star chest is earned and not yet taken. */
  canClaimStarChest(chapter: number): boolean {
    const taken = this.starChestsClaimed(chapter);
    return taken < STAR_CHESTS_PER_CHAPTER && this.totalStars(chapter).have >= this.starChestThresholds(chapter)[taken];
  }

  /**
   * Claims the chapter's next star chest: rolls it (the first-clear band at the chapter's last level, seeded from the
   * player and the milestone, so it is the same chest however often the claim is retried), banks it and marks the
   * milestone taken, paying its gems into the wallet (D20). Null when nothing is claimable.
   */
  claimStarChest(chapter: number, playerId: string, now?: number): StarChestClaim | null {
    if (!this.canClaimStarChest(chapter)) return null;
    const milestone = this.starChestsTaken[chapter];
    const chest = rollChest(this.balance, this.chapterRange(chapter).last, STAR_CHEST_BAND, starChestSeed(playerId, chapter, milestone));
    this.starChestsTaken[chapter] = milestone + 1;
    const gems = starChestGems(this.balance, milestone);
    this.economy.wallet.grantGems(gems, "starChest", now, `chapter${chapter + 1}.${milestone + 1}`);
    return { chest, gear: this.bankChest(chest), gems };
  }
}

/** FNV-1a over (tag, playerId, chapter, milestone). */
export function starChestSeed(playerId: string, chapter: number, milestone: number): bigint {
  const h = new Fnv1a64();
  h.writeString(STAR_CHEST_SEED_TAG);
  h.writeString(playerId);
  h.writeInt(chapter);
  h.writeInt(milestone);
  return h.value;
}
